import React, { useState, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserPlus, Search, X } from 'lucide-react';
import Modal from '../common/Modal';
import Button from '../common/Button';
// eslint-disable-next-line no-unused-vars
import Input from '../common/Input';
import { eventUserAssignmentAPI } from '../../api/endpoints';
import { useToastContext } from '../../context/ToastContext';
import { debounce } from 'lodash';

const UserAssignmentModal = ({ event, isOpen, onClose }) => {
  const queryClient = useQueryClient();
  const toast = useToastContext();
  
  const [selectedUser, setSelectedUser] = useState(null);
  const [userSearch, setUserSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [permissions, setPermissions] = useState({
    role: 'viewer',
    can_manage_attendees: false,
    can_manage_fields: false,
    can_manage_badges: false,
    can_view_analytics: false
  });
  const [isSearching, setIsSearching] = useState(false);

  // Debounced user search
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const searchUsers = useCallback(
    debounce(async (query) => {
      if (query.length < 2) {
        setSearchResults([]);
        setIsSearching(false);
        return;
      }
      
      setIsSearching(true);
      try {
        const response = await eventUserAssignmentAPI.searchUsers(query);
        setSearchResults(response.data || []);
      } catch (error) {
        console.error('Failed to search users:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300),
    []
  );

  // Handle search input change
  const handleSearchChange = (value) => {
    setUserSearch(value);
    if (value !== selectedUser?.name) {
      setSelectedUser(null);
    }
    searchUsers(value);
  };

  // Select user from search results
  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setUserSearch(user.name);
    setSearchResults([]);
  };

  // Clear selected user
  const handleClearUser = () => {
    setSelectedUser(null);
    setUserSearch('');
    setSearchResults([]);
  };

  // Assign user mutation
  const assignUserMutation = useMutation({
    mutationFn: (data) => eventUserAssignmentAPI.create(event.id, data),
    onSuccess: (response) => {
      queryClient.invalidateQueries(['event-user-assignments', event.id]);
      toast.success(response.data.message || 'User assigned successfully');
      onClose();
      // Reset form
      setSelectedUser(null);
      setUserSearch('');
      setSearchResults([]);
      setPermissions({
        role: 'viewer',
        can_manage_attendees: false,
        can_manage_fields: false,
        can_manage_badges: false,
        can_view_analytics: false
      });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to assign user');
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!selectedUser) {
      toast.error('Please select a user to assign');
      return;
    }

    assignUserMutation.mutate({
      user_id: selectedUser.id,
      role: permissions.role,
      permissions: {
        can_manage_attendees: permissions.can_manage_attendees,
        can_manage_fields: permissions.can_manage_fields,
        can_manage_badges: permissions.can_manage_badges,
        can_view_analytics: permissions.can_view_analytics
      }
    });
  };

  const handleClose = () => {
    if (!assignUserMutation.isPending) {
      onClose();
      // Reset form
      setSelectedUser(null);
      setUserSearch('');
      setSearchResults([]);
      setPermissions({
        role: 'viewer',
        can_manage_attendees: false,
        can_manage_fields: false,
        can_manage_badges: false,
        can_view_analytics: false
      });
    }
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={handleClose}
      title="Assign User to Event"
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* User Search */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Search Users
          </label>
          <div className="relative">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={userSearch}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="Search by name or email..."
                className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                disabled={assignUserMutation.isPending}
              />
              {selectedUser && (
                <button
                  type="button"
                  onClick={handleClearUser}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            
            {/* Search Results */}
            {searchResults.length > 0 && !selectedUser && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {searchResults.map((user) => (
                  <div
                    key={user.id}
                    onClick={() => handleSelectUser(user)}
                    className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                  >
                    <div className="font-medium text-gray-900">{user.name}</div>
                    <div className="text-sm text-gray-500">{user.email}</div>
                    <div className="text-xs text-blue-600 capitalize">{user.role?.replace('_', ' ')}</div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Loading indicator */}
            {isSearching && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
              </div>
            )}
          </div>
        </div>

        {/* Selected User Display */}
        {selectedUser && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 mb-2">Selected User</h4>
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium text-gray-900">{selectedUser.name}</div>
                <div className="text-sm text-blue-700">{selectedUser.email}</div>
                <div className="text-xs text-blue-600 capitalize">{selectedUser.role?.replace('_', ' ')}</div>
              </div>
            </div>
          </div>
        )}

        {/* Role Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Event Role
          </label>
          <select
            value={permissions.role}
            onChange={(e) => setPermissions(prev => ({ ...prev, role: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            disabled={assignUserMutation.isPending}
          >
            <option value="viewer">Viewer - Can view event data</option>
            <option value="usher">Usher - Can check-in attendees</option>
            <option value="admin">Admin - Full event management</option>
          </select>
        </div>

        {/* Specific Permissions */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Specific Permissions
          </label>
          <div className="space-y-3">
            {[
              { key: 'can_manage_attendees', label: 'Manage Attendees', desc: 'Add, edit, delete attendees' },
              { key: 'can_manage_fields', label: 'Manage Fields', desc: 'Configure custom fields' },
              { key: 'can_manage_badges', label: 'Manage Badges', desc: 'Design and print badges' },
              { key: 'can_view_analytics', label: 'View Analytics', desc: 'Access event statistics' }
            ].map((perm) => (
              <label key={perm.key} className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={permissions[perm.key]}
                  onChange={(e) => setPermissions(prev => ({ 
                    ...prev, 
                    [perm.key]: e.target.checked 
                  }))}
                  className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  disabled={assignUserMutation.isPending}
                />
                <div className="flex-1">
                  <div className="font-medium text-gray-900">{perm.label}</div>
                  <div className="text-sm text-gray-500">{perm.desc}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button 
            type="button"
            variant="outline" 
            onClick={handleClose}
            disabled={assignUserMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            loading={assignUserMutation.isPending}
            disabled={!selectedUser}
            className="gap-2"
          >
            <UserPlus className="h-4 w-4" />
            Assign User
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default UserAssignmentModal;
